import { isProcessedPackageActive, ProcessMethod, ProcessedPackage } from "./processedCatalog";

export type PackagingLanguage = "sv" | "en";
export const PACKAGING_LANGUAGES: Array<{ value: PackagingLanguage; label: string }> = [
  { value: "sv", label: "Svenska" }, { value: "en", label: "Engelska" },
];
export const isPackagingLanguage = (value: unknown): value is PackagingLanguage => value === "sv" || value === "en";
export const packagingLanguageLabel = (value: unknown): string => PACKAGING_LANGUAGES.find((option) => option.value === value)?.label || "Ej angivet";

export interface ProcessedStockProductLink {
  name_sv: string;
  name_en: string;
  linkedParents: string[];
  processMethod: ProcessMethod;
}

export interface ProcessedStockSourceLink {
  productId: string;
  product: string;
  process?: ProcessMethod;
  sourceType?: "grown_by_us" | "external";
  externalProductName?: string | null;
}

export interface ProcessedStockLink {
  firebaseId: string;
  packageId?: string;
  productId?: string;
  sourceBatchRef?: string;
  sourceBatchRefs?: string[];
}

export const PROCESSED_PUBLIC_STOCK_COLLECTION = "products_freezedry_package_batch";

export interface ProcessedStockTrace {
  productId: string;
  packageId: string;
  processDate: string;
  processedBy: string;
  packingDate: string;
  packedBy: string;
  bestBeforeDate?: string;
  createdAt: number;
}

export interface PublicProcessedStockTrace {
  productId: string;
  productDocId: string;
  packageId: string;
  processDate: string;
  processedBy: string;
  packingDate: string;
  packedBy: string;
  bestBeforeDate: string;
  batchLabel: string;
  isPublic: true;
  createdAt: number;
}

export function sourceRefsOf(entry: Pick<ProcessedStockLink, "sourceBatchRef" | "sourceBatchRefs">): string[] {
  return Array.from(new Set([...(entry.sourceBatchRefs || []), entry.sourceBatchRef].filter((ref): ref is string => !!ref)));
}

export function isValidDate(value: string): boolean {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(value + "T00:00:00Z");
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function validTraceDates(processDate: string, packingDate: string, bestBeforeDate = ""): boolean {
  return isValidDate(processDate) && isValidDate(packingDate) && processDate <= packingDate &&
    (!bestBeforeDate || (isValidDate(bestBeforeDate) && packingDate <= bestBeforeDate));
}

export function publicProcessedStockTrace(
  stock: ProcessedStockTrace, productDocId: string, productIsPublic: boolean,
): PublicProcessedStockTrace | null {
  const validId = (id: unknown) => typeof id === "string" && !!id && id.length <= 128 && id === id.trim() && !id.includes("/") && id !== "." && id !== "..";
  if (!productIsPublic || !validId(stock.productId) || !validId(stock.packageId) || !productDocId ||
    !validTraceDates(stock.processDate, stock.packingDate, stock.bestBeforeDate)) return null;
  return {
    productId: stock.productId,
    productDocId,
    packageId: stock.packageId,
    processDate: stock.processDate,
    processedBy: typeof stock.processedBy === "string" && stock.processedBy.length <= 120 ? stock.processedBy : "",
    packingDate: stock.packingDate,
    packedBy: typeof stock.packedBy === "string" && stock.packedBy.length <= 120 ? stock.packedBy : "",
    bestBeforeDate: stock.bestBeforeDate || "",
    batchLabel: stock.packingDate.slice(2).replace(/-/g, ""),
    isPublic: true,
    createdAt: Number.isSafeInteger(stock.createdAt) && stock.createdAt >= 0 ? stock.createdAt : 0,
  };
}

function normalizedSourceName(name: string): string {
  return name.normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("sv-SE").replace(/^(frystorkad|frystorkade|torkad|torkade|freeze.dried|dehydrated)\s+/, "")
    .replace(/[^a-z0-9]+/g, " ").trim();
}

export function matchesProcessedSource(product: ProcessedStockProductLink, batch: ProcessedStockSourceLink): boolean {
  if ((batch.process || "freeze_dried") !== product.processMethod) return false;
  if (product.linkedParents.includes(batch.productId)) return true;
  if (batch.productId !== "external" && batch.sourceType !== "external") return false;
  const sourceName = normalizedSourceName(batch.externalProductName || batch.product || batch.productId);
  return [product.name_sv, product.name_en].some((name) => {
    const productName = normalizedSourceName(name);
    return !!sourceName && !!productName &&
      (sourceName === productName || sourceName.startsWith(productName + " ") || productName.startsWith(sourceName + " "));
  });
}

export function availableProcessedPackages(
  packages: ProcessedPackage[],
  stockEntries: ProcessedStockLink[],
  productId: string,
  sourceBatchRef: string,
  editingEntryId?: string,
  excludedPackageIds: string[] = [],
): ProcessedPackage[] {
  const currentPackageId = stockEntries.find((entry) => entry.firebaseId === editingEntryId)?.packageId;
  const unresolvedLegacyEntry = stockEntries.some((entry) => !entry.packageId && entry.productId === productId &&
    sourceRefsOf(entry).includes(sourceBatchRef) && entry.firebaseId !== editingEntryId);
  if (unresolvedLegacyEntry) return [];
  return packages.filter((pkg) => pkg.parentId === productId && pkg.packageType !== "box" &&
    !excludedPackageIds.includes(pkg.id) &&
    (isProcessedPackageActive(pkg, packages) || pkg.id === currentPackageId));
}
